import {
  LEGACY_OBJECT_COLORS,
  OBJECT_COLORS,
  type LegacyObjectColor,
  type NeutralObjectColor,
  type ObjectColor,
  type ObjectIcon,
} from '@dataroom/core';
import type { IconName } from './icons';

/** `var(--name)` of a design token; inline styles follow the active theme (light or dark). */
export function cssVar(name: string): string {
  return `var(--${name})`;
}

/**
 * The design tokens for inline styles and SVG. Every value is a CSS variable, so it follows the
 * theme; the colours themselves live only in `styles/tokens.css`.
 */
export const colors = {
  rail: cssVar('rail'),
  railActive: cssVar('rail-active'),
  railText: cssVar('rail-text'),
  railMuted: cssVar('rail-muted'),
  railBorder: cssVar('rail-border'),
  ink: cssVar('ink'),
  ink2: cssVar('ink-2'),
  ink3: cssVar('ink-3'),
  ink4: cssVar('ink-4'),
  border: cssVar('border'),
  borderStrong: cssVar('border-strong'),
  canvas: cssVar('canvas'),
  surface: cssVar('surface'),
  subtle: cssVar('subtle'),
  neutralChip: cssVar('neutral-chip'),
  accent: cssVar('accent'),
  accentHover: cssVar('accent-hover'),
  accentInk: cssVar('accent-ink'),
  accentTint: cssVar('accent-tint'),
  accentBorder: cssVar('accent-border'),
  focusRing: cssVar('focus-ring'),
  onAccent: cssVar('on-accent'),
  selection: cssVar('selection'),
  selectionTint: cssVar('selection-tint'),
  ok: cssVar('ok'),
  okBg: cssVar('ok-bg'),
  warn: cssVar('warn'),
  warnBg: cssVar('warn-bg'),
  err: cssVar('err'),
  errBg: cssVar('err-bg'),
  ext: cssVar('ext'),
  extBg: cssVar('ext-bg'),
  comp: cssVar('comp'),
  compBg: cssVar('comp-bg'),
} as const;

/** The accent as a hex value, for colour maths that cannot read CSS (pickers, contrast hints). */
export const ACCENT_HEX = '#444CE7';

export type ObjectColorTokens = {
  fg: string;
  bg: string;
  rail: string;
  /** The neutral colour name (`ui.objectColor.<name>` in the kit catalog, see `objectColorLabel`). */
  name: NeutralObjectColor;
  /**
   * @deprecated The colour's suffix in the older `dm.color.*` catalog (`pink` for rose, `gray` for
   * slate). Use `name` with `objectColorLabel`; removed in wave 3.
   */
  hue: string;
};

const LEGACY_HUE: Partial<Record<NeutralObjectColor, string>> = { rose: 'pink', slate: 'gray' };

function objectColorTokens(color: NeutralObjectColor): ObjectColorTokens {
  return {
    fg: cssVar(`obj-${color}`),
    bg: cssVar(`obj-${color}-bg`),
    rail: cssVar(`obj-${color}-rail`),
    name: color,
    hue: LEGACY_HUE[color] ?? color,
  };
}

/** The neutral object palette (`indigo`, `blue`, …, `slate`) as theme-aware CSS variables. */
export const neutralObjectColors = Object.fromEntries(
  OBJECT_COLORS.map((c) => [c, objectColorTokens(c)]),
) as Record<NeutralObjectColor, ObjectColorTokens>;

/**
 * Object identity colours by stored name: the neutral palette plus, until wave 3, the names stored
 * before it (read-time aliases with the colour of the same hue). `hue` names the colour for people.
 */
export const objectColors: Record<ObjectColor, ObjectColorTokens> = {
  ...neutralObjectColors,
  ...(Object.fromEntries(
    (Object.entries(LEGACY_OBJECT_COLORS) as [LegacyObjectColor, NeutralObjectColor][]).map(([legacy, c]) => [
      legacy,
      neutralObjectColors[c],
    ]),
  ) as Record<LegacyObjectColor, ObjectColorTokens>),
};

/** Object icon names (core) → UI icon set. */
export const objectIcons: Record<ObjectIcon, IconName> = {
  building: 'building',
  person: 'person',
  currency: 'currency',
  calendar: 'calendar',
  mail: 'mail',
  box: 'box',
  list: 'list',
  task: 'task',
  badge: 'badge',
  briefcase: 'briefcase',
  file: 'file',
};

/**
 * Neutral avatar palette for members without a colour. These are stored member colours (hex, data),
 * dark enough for white initials in both themes.
 */
export const avatarPalette = [
  '#444CE7',
  '#6938EF',
  '#067647',
  '#B54708',
  '#107569',
  '#344054',
  '#C11574',
  '#2E90FA',
];
